import { MoreOutlined } from '@ant-design/icons';
import {
  ColorPicker,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Typography,
} from 'antd';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  EditSalesFunnelModalProps,
  FormItemsTypes,
} from '@app/components/EditSalesFunnelModal/types';
import {
  CANCEL,
  MAX_LENGTH_MESSAGE,
  MIN_THREE_SYMBOLS,
  PREFIX,
  SAVE,
  SUCCESS,
  TAGS_COLORS,
} from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  useCreateSalesFunnelStageMutation,
  useUpdateSalesFunnelStageMutation,
} from '@app/services/rtkQuery/SalesFunnelsStages/api';
import type { NFunnels } from '@app/types';

import {
  CATEGORY_SELECT_OPTIONS,
  EDIT_SALES_FUNNEL,
  NEW_SALES_FUNNEL,
  RULES_IS_REQUIRE,
} from '@pages/SalesFunnels/SalesFunnelsRegistry/constants/editSalesFunnelModal';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { useForm } = Form;

export const EditSalesFunnelModal: React.FC<EditSalesFunnelModalProps> = ({
  isEditSalesFunnelModalOpen,
  setIsEditSalesFunnelModalOpen,
  salesFunnelId,
  salesFunnelsList,
  currentSalesFunnelStage,
  setCurrentSalesFunnelStage,
  isEditModal,
  refetchListData,
  headerForm,
  refetchGetSalesFunnelStages,
  currentSalesFunnelStages,
  handleDeleteSalesFunnelStageClick,
  isRegistryComponent,
}) => {
  const [orderSelectOptions, setOrderSelectOptions] = useState<
    | NFunnels.SalesFunnelStageFilterListDto[]
    | NFunnels.SalesFunnelStageListDto[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<
    number | undefined
  >();

  const {
    userAccessRights: {
      SalesFunnels: { IsStageDelete },
    },
  } = useNewUserAccessRights();

  const { activeBranchId } = useUserPrint();

  const [createSalesFunnelStage, { isLoading: isCreateStageLoading }] =
    useCreateSalesFunnelStageMutation();
  const [updateSalesFunnelStage, { isLoading: isUpdateStageLoading }] =
    useUpdateSalesFunnelStageMutation();

  const [form] = useForm();
  const wasEditSalesFunnelModalOpenRef = useRef(isEditSalesFunnelModalOpen);

  const { showNotification } = useNotification();

  const getOrderOptions = useCallback(
    (value?: number) => {
      if (salesFunnelsList) {
        const currentSalesFunnel = salesFunnelsList.find(
          (item) => item.Id === salesFunnelId
        );
        if (currentSalesFunnel) {
          const currentSalesFunnelStage = currentSalesFunnel.Stages.filter(
            (item) => item.Category === value
          );
          currentSalesFunnel && setOrderSelectOptions(currentSalesFunnelStage);
        }
      } else if (currentSalesFunnelStages) {
        setOrderSelectOptions(
          currentSalesFunnelStages.filter((item) => item.Category === value)
        );
      }
    },
    [currentSalesFunnelStages, salesFunnelId, salesFunnelsList]
  );

  const getOrderByStageId = useCallback(
    (stageId?: number) => {
      if (stageId === undefined) {
        return undefined;
      }

      return orderSelectOptions.find((item) => item.Id === stageId)?.Order;
    },
    [orderSelectOptions]
  );

  useEffect(() => {
    if (currentSalesFunnelStage) {
      if (isEditModal) {
        form.setFieldsValue({
          Name: currentSalesFunnelStage.Name,
          Description: currentSalesFunnelStage.Description,
          Category: currentSalesFunnelStage.Category,
          Order: currentSalesFunnelStage.Id,
          Color: currentSalesFunnelStage.Color,
        });
      } else {
        form.setFieldsValue({
          Category: currentSalesFunnelStage.Category,
          Order: currentSalesFunnelStage.Id,
          Color: getSalesFunnelStageColor(currentSalesFunnelStage.Category),
        });
      }
      setSelectedCategory(currentSalesFunnelStage.Category);
      getOrderOptions(currentSalesFunnelStage.Category);
    }
  }, [currentSalesFunnelStage, form, getOrderOptions, isEditModal]);

  const getSalesFunnelStageColor = (value: number) => {
    switch (value) {
      case 0:
        return '#d4b106';
      case 1:
        return '#52c41a';
      case 2:
        return '#f5222d';
      default:
        return null;
    }
  };

  const afterModalClose = useCallback(() => {
    form.resetFields();
    setOrderSelectOptions([]);
    setSelectedCategory(undefined);
    setCurrentSalesFunnelStage(undefined);

    if (isRegistryComponent) {
      headerForm.resetFields();
    }
  }, [form, headerForm, isRegistryComponent, setCurrentSalesFunnelStage]);

  useEffect(() => {
    if (wasEditSalesFunnelModalOpenRef.current && !isEditSalesFunnelModalOpen) {
      afterModalClose();
    }

    wasEditSalesFunnelModalOpenRef.current = isEditSalesFunnelModalOpen;
  }, [afterModalClose, isEditSalesFunnelModalOpen]);

  const editModalSaveHandler = useCallback(
    async (id: number, values: FormItemsTypes) => {
      const { Name, Description, Category, Order, Color } = values;
      const resolvedOrder = getOrderByStageId(Order);

      try {
        await updateSalesFunnelStage({
          id,
          ActiveBranchId: activeBranchId,
          data: {
            Name,
            Description: Description ?? '',
            Category,
            Order: resolvedOrder ?? 0,
            Color:
              typeof Color === 'string'
                ? Color
                : Color
                ? Color.toHexString()
                : TAGS_COLORS[0],
          },
        }).unwrap();

        setIsEditSalesFunnelModalOpen(false);
        showNotification('', SUCCESS);
        refetchListData?.();
        refetchGetSalesFunnelStages?.();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      updateSalesFunnelStage,
      activeBranchId,
      getOrderByStageId,
      setIsEditSalesFunnelModalOpen,
      showNotification,
      refetchListData,
      refetchGetSalesFunnelStages,
    ]
  );

  const createModalSaveHandler = useCallback(
    async (values: FormItemsTypes) => {
      const { Name, Description, Category, Order, Color } = values;
      const resolvedOrder = getOrderByStageId(Order);

      try {
        await createSalesFunnelStage({
          Name,
          Description: Description ?? '',
          Category,
          Order: resolvedOrder === undefined ? 0 : resolvedOrder + 1,
          Color:
            typeof Color === 'string'
              ? Color
              : Color
              ? Color.toHexString()
              : TAGS_COLORS[0],
          SalesFunnelId: salesFunnelId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setIsEditSalesFunnelModalOpen(false);
        showNotification('', SUCCESS);
        refetchListData?.();
        refetchGetSalesFunnelStages?.();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      createSalesFunnelStage,
      salesFunnelId,
      activeBranchId,
      getOrderByStageId,
      setIsEditSalesFunnelModalOpen,
      showNotification,
      refetchListData,
      refetchGetSalesFunnelStages,
    ]
  );

  const handleSaveModal = useCallback(
    async (values: FormItemsTypes) => {
      if (isEditModal && currentSalesFunnelStage) {
        editModalSaveHandler(currentSalesFunnelStage.Id, values);
      } else {
        createModalSaveHandler(values);
      }
    },
    [
      editModalSaveHandler,
      createModalSaveHandler,
      currentSalesFunnelStage,
      isEditModal,
    ]
  );

  const dropdownItems = useMemo(
    () => [
      {
        key: 'deleteTask',
        label: (
          <div onClick={handleDeleteSalesFunnelStageClick}>
            Удалить этап воронки
          </div>
        ),
      },
    ],
    [handleDeleteSalesFunnelStageClick]
  );

  const ModalTitle = (
    <div className="edit-funnel-modal-title">
      <Title level={5}>
        {isEditModal ? EDIT_SALES_FUNNEL : NEW_SALES_FUNNEL}
      </Title>
      {isEditModal && isRegistryComponent && IsStageDelete && (
        <Dropdown menu={{ items: dropdownItems }}>
          <MoreOutlined />
        </Dropdown>
      )}
    </div>
  );

  const isDisabled = useMemo(
    () => isCreateStageLoading || isUpdateStageLoading,
    [isCreateStageLoading, isUpdateStageLoading]
  );

  return (
    <Modal
      open={isEditSalesFunnelModalOpen}
      onCancel={() => setIsEditSalesFunnelModalOpen(false)}
      cancelText={CANCEL}
      okText={SAVE}
      title={ModalTitle}
      className={`${PREFIX}edit-funnel-modal`}
      onOk={form.submit}
      okButtonProps={{ loading: isDisabled }}
      cancelButtonProps={{ disabled: isDisabled }}
    >
      <Form
        form={form}
        onFinish={handleSaveModal}
        initialValues={{ Color: TAGS_COLORS[4] }}
      >
        <div className="container">
          <div className="item-container">
            <Text strong className="label-require">
              Название этапа воронки
            </Text>
            <Form.Item
              name="Name"
              rules={[
                ...RULES_IS_REQUIRE,
                {
                  min: 3,
                  message: MIN_THREE_SYMBOLS,
                },
                {
                  max: 120,
                  message: MAX_LENGTH_MESSAGE,
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </div>

          <div className="item-container">
            <Text strong className="label">
              Описание этапа воронки
            </Text>
            <Form.Item
              name="Description"
              rules={[
                {
                  max: 600,
                  message: MAX_LENGTH_MESSAGE,
                },
              ]}
            >
              <TextArea rows={4} />
            </Form.Item>
          </div>

          <div className="item-container">
            <Text strong className="label-require">
              Категория
            </Text>
            <Form.Item name="Category" rules={RULES_IS_REQUIRE}>
              <Select
                options={CATEGORY_SELECT_OPTIONS}
                onChange={(option) => {
                  setSelectedCategory(option);
                  getOrderOptions(option);
                }}
              />
            </Form.Item>
          </div>

          <div className="item-container">
            <Text strong className="label">
              Добавить после
            </Text>
            <Form.Item name="Order">
              <Select
                options={orderSelectOptions.map((item) => ({
                  key: String(item.Id),
                  label: item.Name,
                  value: item.Id,
                }))}
                disabled={!selectedCategory && !isEmpty(selectedCategory)}
                placeholder={
                  !selectedCategory ? (
                    <Text type="secondary">Сначала выберите категорию</Text>
                  ) : (
                    ''
                  )
                }
              />
            </Form.Item>
          </div>

          <div className="item-container">
            <Text strong className="label">
              Цвет заливки
            </Text>

            <Form.Item name="Color" className="mg-0">
              <ColorPicker presets={[{ label: '', colors: TAGS_COLORS }]} />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
